import React, { useEffect, useState } from 'react';
import { 
  Box, 
  Button, 
  TextField, 
  MenuItem, 
  FormControl, 
  InputLabel, 
  Select, 
  Typography,
  Paper,
  Divider,
  CircularProgress,
  useTheme,
  Grid
} from '@mui/material';
import MuiAlert, { AlertProps } from '@mui/material/Alert';
import { authenticationService } from "src/services";
import { get, post } from 'src/services/httpClient.service';
import { useWalletContext } from 'src/components/walletcontexthook';
import { getResponsiveStyles, isMobileScreen } from 'src/common/utils/responsive';
import { useSnackbar } from 'src/components/snackbar/useSnackbar';

// Define interfaces for your data structures
interface PaymentData {
    amount: string;
    currency: string;
    paymentDate: string;
    paymentGateway: string;
    paymentMethod: string;
    paymentMode: string;
    paymentStatus: string;
    transactionId: string;
    franchiseId: string;
    description: string;
    franchisePhone: string;
    franchisePhoneCode: string;
    companyId?: string;
}

interface Franchise {
    id: string;
    name: string;
    status: boolean;
    mobileNo: string;
    phone?: string;
    phoneCode?: string;
    email?: string;
    vendorId?: string;
    contactNumber?: string;
    countryCode?: string;
    companyId?: string;
}

interface AddPaymentProps {
    handleCloseDialog: () => void;
    fetchData: () => void;
}

interface Errors {
    amount: boolean;
    currency: boolean;
    paymentDate: boolean;
    paymentGateway: boolean;
    paymentStatus: boolean;
    franchiseId: boolean;
    description: boolean;
    franchisePhone: boolean;
    franchisePhoneCode: boolean;
}

const Alert = React.forwardRef<HTMLDivElement, AlertProps>(function Alert(
    props,
    ref,
) {
    return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

const AddOnlineFranchisePayment: React.FC<AddPaymentProps> = ({ handleCloseDialog, fetchData }) => {
    const theme = useTheme();
    const styles = getResponsiveStyles(theme);
    const [isMobile, setIsMobile] = useState(isMobileScreen(theme));
    const { showSnackbar } = useSnackbar();
    
    const { vendorId, franchiseId } = authenticationService.getVendorFranchiseIds();
    const accountType = localStorage.getItem('accountType');
    const { fetchFranchisees } = useWalletContext();
    const API_URL = process.env.REACT_APP_BASE_API_URL;
    
    const [paymentData, setPaymentData] = useState<PaymentData>({
        amount: '',
        currency: 'INR',
        paymentDate: new Date().toISOString().split('T')[0],
        paymentGateway: '',
        paymentMethod: '',
        paymentMode: 'ONLINE',
        paymentStatus: 'CREATED',
        transactionId: '',
        franchiseId: '',
        description: '',
        franchisePhone: '',
        franchisePhoneCode: '+91',
        companyId: ''
    });

    const [errors, setErrors] = useState<Errors>({
        amount: false,
        currency: false,
        paymentDate: false,
        paymentGateway: false,
        paymentStatus: false,
        franchiseId: false,
        description: false,
        franchisePhone: false,
        franchisePhoneCode: false
    });

    const [franchises, setFranchises] = useState<Franchise[]>([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(isMobileScreen(theme));
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [theme]);

    const validateForm = () => {
        const newErrors = { ...errors };
        let valid = true;

        const required = [
            "amount", "currency", "paymentDate",
            "paymentGateway", "paymentStatus",
            "franchiseId", "description",
            "franchisePhone", "franchisePhoneCode",
        ];

        required.forEach((field) => {
            // @ts-ignore
            newErrors[field] = !paymentData[field];
            if (!paymentData[field]) valid = false;
        });

        // Additional validation for amount - check if it's a valid number
        if (paymentData.amount) {
            const amountNum = parseFloat(paymentData.amount);
            if (isNaN(amountNum) || amountNum <= 0) {
                newErrors.amount = true;
                valid = false;
            }
        }

        setErrors(newErrors);
        return valid;
    };

    const fetchFranchiseList = async () => {
        try {
            let response = null;

            if (accountType?.trim() === "OWNER") {
                response = await get(`${API_URL}/api/franchisees/vendor/${vendorId}/fetchAll`);
                console.log("Owner Franchise list:", response);
                setFranchises(Array.isArray(response.data) ? response.data : []);
            } else {
                response = await get(`${API_URL}/api/franchisees/vendor/${vendorId}/getFranchiseeById?id=${franchiseId}`);
                console.log("Single Franchise:", response);
                setFranchises(response.data ? [response.data] : []);
            }
        } catch (err) {
            console.error("Error fetching franchises:", err);
        }
    };

    useEffect(() => {
        fetchFranchiseList();
    }, []);

    const handleChange = (e: any) => {
        const { name, value } = e.target;
        setPaymentData(prev => ({ ...prev, [name as string]: value as string }));
        
        // Clear error for this field
        if (errors[name as keyof Errors]) {
            setErrors(prev => ({ ...prev, [name]: false }));
        }

        if (name === "franchiseId") {
            const selected = franchises.find(f => f.id === value);

            if (selected) {
                setPaymentData(prev => ({
                    ...prev,
                    franchiseId: selected.id,
                    paymentGateway: "RazorPay",
                    franchisePhone: selected.mobileNo || selected.phone || "",
                    franchisePhoneCode: selected.phoneCode || "+91",
                    companyId: selected.companyId || ""
                }));
            }
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        
        if (!validateForm()) {
            showSnackbar("Please fill all fields correctly.", 'error');
            return;
        }

        setLoading(true);

        try {
            const selected = franchises.find(f => f.id === paymentData.franchiseId);

            // Parse amount and validate
            const amountValue = parseFloat(paymentData.amount);
            if (isNaN(amountValue) || amountValue <= 0) {
                showSnackbar("Please enter a valid amount.", 'error');
                setLoading(false);
                return;
            }

            const payload = {
                vendorId,
                franchiseId: selected?.id,
                franchiseName: selected?.name,
                franchiseEmail: selected?.email,
                franchisePhone: paymentData.franchisePhone,
                franchisePhoneCode: paymentData.franchisePhoneCode,
                paymentType: "OFFLINE",
                paymentGateway: paymentData.paymentGateway.toUpperCase(),
                paymentStatus: paymentData.paymentStatus.toUpperCase(),
                currency: paymentData.currency,
                amount: amountValue,
                description: paymentData.description,
                paymentDate: new Date(paymentData.paymentDate).toISOString(),
                companyId: paymentData.companyId,
                customer: {
                    name: selected?.name,
                    email: selected?.email,
                    contact: paymentData.franchisePhoneCode + paymentData.franchisePhone
                }
            };

            console.log('Sending payload:', payload);

            const response = await post(`${API_URL}/api/franchise-payments/makeOnlinePayment`, payload);
            
            console.log('Payment response:', response.data);
            
            const paymentResponse = response.data;
            
            if (paymentResponse && paymentResponse.paymentUrl) {
                showSnackbar('Payment link created successfully!', 'success');
                
                // Open the payment link in a new tab
                window.open(paymentResponse.paymentUrl, "_blank", "noopener,noreferrer");
                
                // Refresh data and close dialog
                fetchFranchisees();
                fetchData();
                setTimeout(() => handleCloseDialog(), 1500);
                
            } else {
                console.error('Invalid response structure:', paymentResponse);
                throw new Error('Invalid response from server - missing payment URL');
            }

        } catch (error: any) {
            console.error('Error adding payment:', error);
            showSnackbar(error?.message || "Failed to add payment. Please try again.", 'error');
        } finally {
            setLoading(false);
        }
    };

    const handlePaymentClick = (paymentResponse: any) => {
        const paymentWindow = window.open(
            "",
            "RazorpayQR",
            "width=500,height=600,menubar=no,toolbar=no,location=no,status=no,scrollbars=no,resizable=no"
        );
        console.log("paymentResponse:::", paymentResponse);
        paymentWindow?.document.write(`
            <html>
            <head><title>Scan QR</title></head>
            <body style="display:flex;justify-content:center;align-items:center;height:100vh;">
                <img src="${paymentResponse.qrImageUrl}" width="300"/>
            </body>
            </html>
        `);

        window.addEventListener("message", (event) => {
            if (event.data.status === "SUCCESS") {
                paymentWindow?.close();
                window.location.reload();
            }
        });
    };

    //const paymentGateways = ['Stripe', 'RazorPay', 'PayPal', 'PayU', 'CCAvenue'];
    const paymentGateways = ['RazorPay'];
  //const paymentGateways = ['RazorPay', 'PhonePe', 'GPay', 'CashFree', 'PayTm', 'PayU', 'PayPal', 'CCAvenue', 'Stripe'];

    return (
        <>
            <Paper elevation={0} sx={{ p: { xs: 2, sm: 3 }, maxHeight: '80vh', overflow: 'auto' }}>
                <Box component="form" onSubmit={handleSubmit}>
                    <Typography 
                        variant={isMobile ? "h6" : "h5"} 
                        gutterBottom 
                        sx={{ 
                            fontWeight: 600,
                            color: 'primary.main',
                            mb: 2,
                            borderBottom: '2px solid',
                            borderColor: 'primary.main',
                            pb: 1,
                            display: 'inline-block'
                        }}
                    >
                        Add Online Franchise Payment
                    </Typography>


                    <Divider sx={{ mb: 3 }} />

                    <Grid container spacing={isMobile ? 2 : 3}>
                        {/* Franchise Select */}
                        <Grid item xs={12} sm={12}>
                            <FormControl fullWidth error={errors.franchiseId}>
                                <InputLabel>Franchise *</InputLabel>
                                <Select
                                    name="franchiseId"
                                    label="Franchise *"
                                    value={paymentData.franchiseId}
                                    onChange={handleChange}
                                    disabled={loading}
                                    MenuProps={{
                                        PaperProps: {
                                            style: {
                                                maxHeight: 300,
                                            },
                                        },
                                    }}
                                >
                                    {franchises.map((f) => (
                                        <MenuItem key={f.id} value={f.id}>
                                            {`${f.name} - ${f.status ? "ACTIVE" : "INACTIVE"}`}
                                        </MenuItem>
                                    ))}
                                </Select>
                                {errors.franchiseId && (
                                    <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                                        Franchise is required.
                                    </Typography>
                                )}
                            </FormControl>
                        </Grid>

                        {/* Franchise Name - Read Only */}
                        {/* <Grid item xs={12} sm={12}>
                            <TextField
                                label="Franchise Name"
                                fullWidth
                                value={(franchises.find((f) => f.id === paymentData.franchiseId)?.name) || ''}
                                InputProps={{
                                    readOnly: true,
                                }}
                                disabled={loading}
                                size={isMobile ? "small" : "medium"}
                            />
                        </Grid> */}

                        {/* Franchise Email - Read Only */}
                        {/* <Grid item xs={12} sm={6}>
                            <TextField
                                label="Franchise Email"
                                fullWidth
                                value={(franchises.find((f) => f.id === paymentData.franchiseId)?.email) || ''}
                                InputProps={{
                                    readOnly: true,
                                }}
                                disabled={loading}
                                size={isMobile ? "small" : "medium"}
                            />
                        </Grid> */}

                        {/* Phone Code */}
                        {/* <Grid item xs={12} sm={3}>
                            <FormControl fullWidth error={errors.franchisePhoneCode}>
                                <InputLabel>Phone Code *</InputLabel>
                                <Select
                                    name="franchisePhoneCode"
                                    value={paymentData.franchisePhoneCode}
                                    onChange={handleChange}
                                    label="Phone Code *"
                                    disabled={loading}
                                >
                                    <MenuItem value="+91">+91 (India)</MenuItem>
                                    <MenuItem value="+1">+1 (USA)</MenuItem>
                                    <MenuItem value="+44">+44 (UK)</MenuItem>
                                    <MenuItem value="+61">+61 (Australia)</MenuItem>
                                    <MenuItem value="+971">+971 (UAE)</MenuItem>
                                </Select>
                                {errors.franchisePhoneCode && (
                                    <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                                        Phone code is required.
                                    </Typography>
                                )}
                            </FormControl>
                        </Grid> */}

                        {/* Phone */}
                        {/* <Grid item xs={12} sm={6}>
                            <TextField
                                label="Phone *"
                                name="franchisePhone"
                                type="tel"
                                fullWidth
                                value={paymentData.franchisePhone}
                                onChange={handleChange}
                                error={errors.franchisePhone}
                                helperText={errors.franchisePhone ? "Phone is required" : ""}
                                disabled={loading}
                                size={isMobile ? "small" : "medium"}
                            />
                        </Grid> */}

                        {/* Payment Gateway */}
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth error={errors.paymentGateway}>
                                <InputLabel>Payment Gateway *</InputLabel>
                                <Select
                                    name="paymentGateway"
                                    value={paymentData.paymentGateway}
                                    onChange={handleChange}
                                    label="Payment Gateway *"
                                    disabled={loading}
                                >
                                    <MenuItem value="">Select Gateway</MenuItem>
                                    {paymentGateways.map((gateway) => (
                                        <MenuItem key={gateway} value={gateway}>{gateway}</MenuItem>
                                    ))}
                                </Select>
                                {errors.paymentGateway && (
                                    <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                                        Payment gateway is required.
                                    </Typography>
                                )}
                            </FormControl>
                        </Grid>

                        {/* Payment Date */}
                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="Payment Date *"
                                name="paymentDate"
                                type="date"
                                fullWidth
                                InputLabelProps={{ shrink: true }}
                                value={paymentData.paymentDate}
                                onChange={handleChange}
                                error={errors.paymentDate}
                                helperText={errors.paymentDate ? 'Payment date is required.' : ''}
                                disabled={loading}
                                size={isMobile ? "small" : "medium"}
                            />
                        </Grid>

                        {/* Amount */}
                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="Amount (in INR) *"
                                name="amount"
                                type="number"
                                fullWidth
                                value={paymentData.amount}
                                onChange={handleChange}
                                error={errors.amount}
                                helperText={errors.amount ? "Please enter a valid amount greater than 0." : ""}
                                disabled={loading}
                                size={isMobile ? "small" : "medium"}
                                InputProps={{
                                    startAdornment: <Typography sx={{ mr: 0.5 }}>₹</Typography>,
                                }}
                            />
                        </Grid>
                        
                        {/* Currency */}
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth error={errors.currency}>
                                <InputLabel>Currency *</InputLabel>
                                <Select
                                    name="currency"
                                    value={paymentData.currency}
                                    onChange={handleChange}
                                    label="Currency *"
                                    disabled={loading}
                                >
                                    <MenuItem value="INR">INR (Indian Rupee)</MenuItem>
                                    <MenuItem value="USD">USD (US Dollar)</MenuItem>
                                    <MenuItem value="EUR">EUR (Euro)</MenuItem>
                                    <MenuItem value="GBP">GBP (British Pound)</MenuItem>
                                </Select>
                                {errors.currency && (
                                    <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                                        Currency is required.
                                    </Typography>
                                )}
                            </FormControl>
                        </Grid>

                        

                        

                        

                        {/* Description */}
                        <Grid item xs={12}>
                            <TextField
                                label="Description *"
                                name="description"
                                fullWidth
                                multiline
                                rows={isMobile ? 3 : 4}
                                value={paymentData.description}
                                onChange={handleChange}
                                error={errors.description}
                                helperText={errors.description ? 'Description is required.' : ''}
                                disabled={loading}
                                size={isMobile ? "small" : "medium"}
                                placeholder="Enter payment description or purpose"
                            />
                        </Grid>
                    </Grid>

                    {/* Buttons */}
                    <Box 
                        sx={{ 
                            display: 'flex', 
                            justifyContent: 'flex-end', 
                            gap: 2, 
                            mt: 4,
                            flexDirection: isMobile ? 'column-reverse' : 'row'
                        }}
                    >
                        <Button
                            variant="outlined"
                            onClick={handleCloseDialog}
                            disabled={loading}
                            fullWidth={isMobile}
                            sx={{ py: isMobile ? 1 : 1.5 }}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            variant="contained"
                            color="primary"
                            disabled={loading}
                            fullWidth={isMobile}
                            sx={{ py: isMobile ? 1 : 1.5 }}
                        >
                            {loading ? <CircularProgress size={24} /> : 'Create Payment Link'}
                        </Button>
                    </Box>

                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2, textAlign: 'center' }}>
                        * Required fields
                    </Typography>
                </Box>
            </Paper>
        </>
    );
};

export default AddOnlineFranchisePayment;